import { useCallback, useState } from 'react'

import { Progress } from '@agro-club/agroclub-shared'

import { apiClient } from 'modules/utils/httpClient'
import { useSingleEntity } from 'modules/domain/common/hooks'
import { useAProgress } from 'hooks/useAProgress'

export type Comment = {
  id: string
  comment: string
  author: string
  created: string
  is_pinned: boolean
  modified: string
  last_modified_author: string
}

export const useComments = (url: string, onSuccess: () => void, commentId?: string) => {
  const [sendProgress, setSendProgress] = useState<Progress>(Progress.IDLE)
  const [isLoadSystem, setIsLoadSystem] = useState(false)
  const [getCommonProgress, commonComments = [], refetchCommon] = useSingleEntity<Comment[]>(`${url}?type=common`)
  const [getSystemProgress, systemComments = [], refetchSystem] = useSingleEntity<Comment[]>(
    isLoadSystem ? `${url}?type=system` : undefined,
  )

  const [unpinProgress, unpinComment] = useAProgress((unpinId) => apiClient.put<Comment>(`${url}${unpinId}/unpin/`), {
    eventName: 'unpinComment',
    onSuccess: async () => await refetchCommon(true),
  })

  const addComment = useCallback(
    async (comment: string, isPin?: boolean) => {
      try {
        setSendProgress(Progress.WORK)
        const resp = await apiClient.post<Comment>(url, { comment })

        if (isPin && resp.id) {
          await apiClient.put<Comment>(`${url}${resp.id}/pin/`)
        }
        setSendProgress(Progress.SUCCESS)
        refetchCommon(true)
        onSuccess()
      } catch (e) {
        setSendProgress(Progress.ERROR)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [url],
  )

  const editPinnedComment = async (comment: string) => {
    try {
      setSendProgress(Progress.WORK)
      await apiClient.put<Comment>(`${url}${commentId}/`, { comment })
      setSendProgress(Progress.SUCCESS)
      refetchCommon(true)
      onSuccess()
    } catch (e) {
      setSendProgress(Progress.ERROR)
    }
  }

  return {
    addComment,
    commonComments,
    sendProgress,
    getCommonProgress,
    getSystemProgress,
    systemComments,
    refetchSystem,
    editPinnedComment,
    setIsLoadSystem,
    unpinComment,
    unpinProgress,
  }
}
